import React, { useState, useEffect } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import StatCard from "../components/ui/StatCard";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import TabBar from "../components/ui/TabBar";
import FormField from "../components/ui/FormField";
import Alert from "../components/ui/Alert";
import DataTable from "../components/ui/DataTable";
import { statusColor } from "../utils/helpers";
import useAuth from "../hooks/useAuth";
import { useSeason } from "../context/SeasonContext";
import { FiRefreshCw } from "react-icons/fi";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "investment", label: "Investments" },
  { id: "payments", label: "Payments" },
  { id: "purchase", label: "Purchase" },
];

export default function Accounts() {
  const token = sessionStorage.getItem("maaze_token");
  const { refreshToken } = useAuth();

  const [tab, setTab] = useState("overview");

  const [ledger, setLedger] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [stats, setStats] = useState({
    investment: 0,
    expenses: 0,
    balance: 0,
    purchase: 0,
  });

  const [investmentForm, setInvestmentForm] = useState({
    season_id: "",
    amount: "",
    investment_date: "",
    mode: "",
    receipt_no: "",
    tranche_no: "",
    bank: "",
    reference_no: "",
    notes: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    season_id: "",
    entry_date: "",
    entry_type: "DR",
    category: "",
    amount: "",
    description: "",
  });

  const [purchaseForm, setPurchaseForm] = useState({
    season_id: "",
    material_id: "",
    supplier_name: "",
    quantity_kg: "",
    rate_per_kg: "",
    purchase_date: "",
    invoice_no: "",
    payment_status: "Pending",
  });

  /* ================= FETCH DATA ================= */

  const fetchSeasons = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/seasons",
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setSeasons(res.data?.data || []);
  };

  const fetchMaterials = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/materials",
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setMaterials(res.data?.data || []);
  };

  const fetchLedger = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/finance/payments",
      { headers: { Authorization: `Bearer ${token}` } },
    );

    const data = res.data?.data || [];
    setLedger(data);
  };

  const fetchPurchases = async () => {
    try {
      const res = await axios.get(
        "https://maize.norissolutions.in/Php/public/api/finance/purchases",
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = res.data?.data?.purchases;
      setPurchases(Array.isArray(data) ? data : []);
    } catch {
      setPurchases([]);
    }
  };

  const fetchInvestments = async () => {
    try {
      const res = await axios.get(
        "https://maize.norissolutions.in/Php/public/api/finance/investments",
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = res.data?.data;
      setInvestments(Array.isArray(data) ? data : []);
    } catch {
      setInvestments([]);
    }
  };

  const fetchFinanceCards = async () => {
    try {
      const res = await axios.get(
        `https://maize.norissolutions.in/Php/public/api/dashboard/finance-cards`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data?.data;
      if (data) {
        setStats({
          investment: data.total_investment || 0,
          expenses: data.total_expenses || 0,
          balance: data.net_balance || 0,
          purchase: data.raw_purchase || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch finance cards:", error);
    }
  };

  useEffect(() => {
    fetchLedger();
    fetchPurchases();
    fetchInvestments();
    fetchSeasons();
    fetchMaterials();
    fetchFinanceCards();
  }, []);

  /* ================= SAVE FUNCTIONS ================= */

  const saveInvestment = async () => {
    await axios.post(
      "https://maize.norissolutions.in/Php/public/api/finance/investments",
      investmentForm,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    fetchInvestments();
    fetchLedger();
    fetchFinanceCards();
  };

  const savePayment = async () => {
    await axios.post(
      "https://maize.norissolutions.in/Php/public/api/finance/payments",
      paymentForm,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    fetchLedger();
    fetchFinanceCards();
  };

  const savePurchase = async () => {
    await axios.post(
      "https://maize.norissolutions.in/Php/public/api/finance/purchases",
      purchaseForm,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    fetchPurchases();
    fetchLedger();
    fetchFinanceCards();
  };

  /* ================= TABLE COLUMNS ================= */

  const TX_COLUMNS = [
    { key: "entry_date", label: "Date" },
    {
      key: "entry_type",
      label: "Type",
      render: (v) => <Badge color={statusColor(v)}>{v}</Badge>,
    },
    { key: "description", label: "Description" },
    {
      key: "debit",
      label: "Debit (₹)",
      render: (v, row) => (row.entry_type === "DR" ? row.amount : "–"),
    },
    {
      key: "credit",
      label: "Credit (₹)",
      render: (v, row) => (row.entry_type === "CR" ? row.amount : "–"),
    },
  ];

  const OVERVIEW_COLUMNS = [
    { key: "date", label: "Date" },
    {
      key: "type",
      label: "Type",
      render: (v) => <Badge color={statusColor(v)}>{v}</Badge>,
    },
    { key: "description", label: "Description" },
    { key: "debit", label: "Debit (₹)" },
    { key: "credit", label: "Credit (₹)" },
  ];

  const INVESTMENT_COLUMNS = [
    { key: "investment_date", label: "Date" },
    { key: "receipt_no", label: "Receipt" },
    { key: "tranche_no", label: "Tranche" },
    { key: "amount", label: "Amount" },
    { key: "mode", label: "Mode" },
    { key: "bank", label: "Bank" },
  ];

  const PAYMENT_COLUMNS = [
    { key: "entry_date", label: "Date" },
    { key: "category", label: "Category" },
    { key: "description", label: "Description" },
    { key: "amount", label: "Amount" },
  ];

  const PURCHASE_COLUMNS = [
    { key: "purchase_date", label: "Date" },
    { key: "supplier_name", label: "Vendor" },
    { key: "material_name", label: "Material" },
    { key: "quantity_kg", label: "Qty" },
    { key: "rate_per_kg", label: "Rate" },
    {
      key: "total",
      label: "Total",
      render: (v, row) => row.quantity_kg * row.rate_per_kg,
    },
  ];

  /* ================= OVERVIEW DATA ================= */

  const overviewRows = [
    ...(Array.isArray(investments) ? investments : []).map((i) => ({
      date: i.investment_date,
      type: "Investment",
      description: i.notes || "Investment",
      debit: "–",
      credit: i.amount,
    })),

    ...(Array.isArray(purchases) ? purchases : []).map((p) => ({
      date: p.purchase_date,
      type: "Purchase",
      description: p.supplier_name,
      debit: p.quantity_kg * p.rate_per_kg,
      credit: "–",
    })),

    ...(Array.isArray(ledger) ? ledger : [])
      .filter((l) => l.entry_type === "DR")
      .map((l) => ({
        date: l.entry_date,
        type: "Payment",
        description: l.description,
        debit: l.amount,
        credit: "–",
      })),
  ];

  return (
    <div>
      <PageHeader
        title="Accounts"
        subtitle="Financial management: investments, payments & receipts"
      >
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {/* ================= OVERVIEW ================= */}

      {tab === "overview" && (
        <>
          <div className="stats-grid mb-24">
            <StatCard
              label="Total Investment"
              value={`₹${stats.investment}`}
              icon="💵"
            />

            <StatCard
              label="Total Expenses"
              value={`₹${stats.expenses}`}
              icon="📤"
              variant="gold"
            />

            <StatCard
              label="Net Balance"
              value={`₹${stats.balance}`}
              icon="💰"
              variant="sky"
            />

            <StatCard
              label="Raw Purchase"
              value={`₹${stats.purchase}`}
              icon="🛒"
            />
          </div>

          <Card
            title="All Financial Activities"
            subtitle="Investments, Payments & Purchases"
            noPad
          >
            <DataTable columns={OVERVIEW_COLUMNS} rows={overviewRows} />
          </Card>
        </>
      )}

      {/* ================= INVESTMENT TAB ================= */}

      {tab === "investment" && (
        <>
          <Card title="Record Investment">
            <Alert variant="info">
              ℹ️ Investments are recorded in the ledger automatically.
            </Alert>

            <div className="form-grid">
              <FormField
                label="Season"
                type="select"
                options={seasons.map((s) => ({
                  label: s.season_name,
                  value: s.season_id,
                }))}
                value={investmentForm.season_id}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    season_id: e.target.value,
                  })
                }
              />

              <FormField
                label="Receipt No."
                value={investmentForm.receipt_no}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    receipt_no: e.target.value,
                  })
                }
              />

              <FormField
                label="Tranche No."
                type="number"
                value={investmentForm.tranche_no}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    tranche_no: e.target.value,
                  })
                }
              />

              <FormField
                label="Date"
                type="date"
                value={investmentForm.investment_date}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    investment_date: e.target.value,
                  })
                }
              />

              <FormField
                label="Amount"
                type="number"
                value={investmentForm.amount}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    amount: e.target.value,
                  })
                }
              />

              <FormField
                label="Mode"
                type="select"
                options={["UPI", "NEFT", "RTGS", "Cheque", "Cash"]}
                value={investmentForm.mode}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    mode: e.target.value,
                  })
                }
              />

              <FormField
                label="Bank"
                value={investmentForm.bank}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    bank: e.target.value,
                  })
                }
              />

              <FormField
                label="Reference No."
                value={investmentForm.reference_no}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    reference_no: e.target.value,
                  })
                }
              />

              <FormField
                label="Notes"
                type="textarea"
                value={investmentForm.notes}
                onChange={(e) =>
                  setInvestmentForm({
                    ...investmentForm,
                    notes: e.target.value,
                  })
                }
              />
            </div>

            <Button style={{ marginTop: 18 }} onClick={saveInvestment}>
              Save Investment
            </Button>
          </Card>

          <Card title="Investment History" noPad>
            <DataTable columns={INVESTMENT_COLUMNS} rows={investments} />
          </Card>
        </>
      )}

      {/* ================= PAYMENTS ================= */}

      {tab === "payments" && (
        <>
          <Card title="Record Payment">
            <div className="form-grid">
              <FormField
                label="Season"
                type="select"
                options={seasons.map((s) => ({
                  label: s.season_name,
                  value: s.season_id,
                }))}
                value={paymentForm.season_id}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    season_id: e.target.value,
                  })
                }
              />

              <FormField
                label="Date"
                type="date"
                value={paymentForm.entry_date}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    entry_date: e.target.value,
                  })
                }
              />

              <FormField
                label="Entry Type"
                type="select"
                options={["CR", "DR"]}
                value={paymentForm.entry_type}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    entry_type: e.target.value,
                  })
                }
              />

              <FormField
                label="Category"
                value={paymentForm.category}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    category: e.target.value,
                  })
                }
              />

              <FormField
                label="Amount"
                type="number"
                value={paymentForm.amount}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    amount: e.target.value,
                  })
                }
              />

              <FormField
                label="Description"
                type="textarea"
                value={paymentForm.description}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    description: e.target.value,
                  })
                }
              />
            </div>

            <Button style={{ marginTop: 18 }} onClick={savePayment}>
              Save Payment
            </Button>
          </Card>

          <Card title="Ledger Transactions" noPad>
            <DataTable columns={TX_COLUMNS} rows={ledger} />
          </Card>
        </>
      )}

      {/* ================= PURCHASE ================= */}

      {tab === "purchase" && (
        <>
          <Card title="Raw Seed Purchase">
            <div className="form-grid">
              <FormField
                label="Season"
                type="select"
                options={seasons.map((s) => ({
                  label: s.season_name,
                  value: s.season_id,
                }))}
                value={purchaseForm.season_id}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    season_id: e.target.value,
                  })
                }
              />

              <FormField
                label="Material"
                type="select"
                options={materials.map((m) => ({
                  label: m.material_name,
                  value: m.material_id,
                }))}
                value={purchaseForm.material_id}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    material_id: e.target.value,
                  })
                }
              />

              <FormField
                label="Vendor"
                value={purchaseForm.supplier_name}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    supplier_name: e.target.value,
                  })
                }
              />

              <FormField
                label="Quantity (kg)"
                type="number"
                value={purchaseForm.quantity_kg}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    quantity_kg: e.target.value,
                  })
                }
              />

              <FormField
                label="Rate"
                type="number"
                value={purchaseForm.rate_per_kg}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    rate_per_kg: e.target.value,
                  })
                }
              />

              <FormField
                label="Date"
                type="date"
                value={purchaseForm.purchase_date}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    purchase_date: e.target.value,
                  })
                }
              />

              <FormField
                label="Invoice"
                value={purchaseForm.invoice_no}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    invoice_no: e.target.value,
                  })
                }
              />

              <FormField
                label="Payment Status"
                type="select"
                options={["Pending", "Paid"]}
                value={purchaseForm.payment_status}
                onChange={(e) =>
                  setPurchaseForm({
                    ...purchaseForm,
                    payment_status: e.target.value,
                  })
                }
              />
            </div>

            <Button style={{ marginTop: 18 }} onClick={savePurchase}>
              Save Purchase
            </Button>
          </Card>

          <Card title="Purchase History" noPad>
            <DataTable columns={PURCHASE_COLUMNS} rows={purchases} />
          </Card>
        </>
      )}
    </div>
  );
}
